import React from 'react';
import { Alert } from '@razorpay/blade/components';
import { routePaths } from 'components/Routes/data';
import { BulkUploadTypes, UploadTypeConfig, UploadTypeConfigCallback } from 'constants/bulkUpload';
import permissions from 'constants/permissions';
import { getContractorText } from 'utils/getContractorText';
import { getPeoplePage } from 'utils/links';

const sheet0Columns: UploadTypeConfig['preview']['columns'] = [
  {
    title: 'Name',
    render: (item) => item.file_row?.commonProperties?.employeeName,
  },
  {
    title: 'Employee ID',
    render: (item) => item.file_row?.commonProperties?.employeeId,
  },
  {
    title: 'Email ID',
    render: (item) => item.file_row?.commonProperties?.email,
  },
  {
    title: 'Date of Hiring',
    render: (item) => item.file_row?.dateOfHiring,
  },
  {
    title: 'Location',
    render: (item) => item.file_row?.location,
  },
  {
    title: 'Phone Number',
    render: (item) => item.file_row?.phone,
  },
  {
    title: 'Gender',
    render: (item) => item.file_row?.gender,
  },
  {
    title: 'Date of Birth',
    render: (item) => item.file_row?.dob,
  },
  {
    title: 'Bank Account',
    render: (item) => item.file_row?.bankAccount,
  },
  {
    title: 'Bank IFSC',
    render: (item) => item.file_row?.bankIfsc,
  },
  {
    title: 'PAN',
    render: (item) => item.file_row?.pan,
  },
  {
    title: 'PF Registration',
    render: (item) => item.file_row?.pfStatus,
  },
  {
    title: 'PF UAN',
    render: (item) => item.file_row?.pfUan,
  },
  {
    title: 'Aadhar Number',
    render: (item) => item.file_row?.aadharNumber,
  },
  {
    title: 'PT Enabled',
    render: (item) => (item.file_row?.ptEnabled ? 'Yes' : 'No'),
  },
  {
    title: 'PT Location',
    render: (item) => item.file_row?.ptLocation,
  },
  {
    title: 'LWF Enabled',
    render: (item) => (item.file_row?.lwfEnabled ? 'Yes' : 'No'),
  },
  {
    title: 'LWF Location',
    render: (item) => item.file_row?.lwfLocation,
  },
  {
    title: 'ESIC Registration Status',
    render: (item) => item.file_row?.esicStatus,
  },
  {
    title: 'ESIC IP Number',
    render: (item) => item.file_row?.esicIpNumber,
  },
  {
    title: 'NPS Enabled',
    render: (item) => item.file_row?.npsEnabled,
  },
  {
    title: 'PRAN Number',
    render: (item) => item.file_row?.pranNumber,
  },
  {
    title: 'Designation',
    render: (item) => item.file_row?.designation,
  },
  {
    title: 'Department',
    render: (item) => item.file_row?.department,
  },
  {
    title: 'Manager',
    render: (item) => item.file_row?.manager,
  },
];

const sheet1Columns: UploadTypeConfig['preview']['columns'] = [
  {
    title: 'Name',
    render: (item) => item.file_row?.commonProperties?.employeeName,
  },
  {
    title: 'Email ID',
    render: (item) => item.file_row?.commonProperties?.email,
  },
  {
    title: 'PAN',
    render: (item) => item.file_row?.pan,
  },
  {
    title: 'Phone Number',
    render: (item) => item.file_row?.phone,
  },
  {
    title: 'Gender',
    render: (item) => item.file_row?.gender,
  },
  {
    title: 'Date of Birth',
    render: (item) => item.file_row?.dob,
  },
  {
    title: 'Bank Account',
    render: (item) => item.file_row?.bankAccount,
  },
  {
    title: 'Bank IFSC',
    render: (item) => item.file_row?.bankIfsc,
  },
];

const config: UploadTypeConfigCallback = (data) => ({
  permissions: [permissions.EMPLOYEE_ADD],
  initiation: {
    feature: 'bulkOnboardingFlag',
    permissions: [],
    pageTitle: 'Bulk Update Employee Details',
    step1Title: '1. Download template',
    step1Subtitle: `Easily update employee or ${
      getContractorText(data).contractor
    } information in bulk by uploading a formatted Excel file.`,
    instructions: [
      'The template has two tabs. Fill out both as needed.',
      'Do not add or remove any columns.',
      'There is a limit of 500 employees per upload',
      'If any field is empty, existing data in that field would be deleted',
      'Avoid using all capital letters for names, addresses, titles, etc.',
      'Mandatory fields are mentioned with * sign',
      'Date should be mentioned in DD/MM/YYYY format',
      "Bank account number should be added with leading '0' if applicable",
      'Do not make any changes or updates to the employee’s email ID.',
    ],
    onSampleTemplateDownload() {
      window.open(`/v2/api/bulk-employee-update/download-template`, '_blank');
    },
    ExtraFields: () => (
      <Alert
        isDismissible={false}
        isFullWidth
        marginTop="spacing.8"
        color="neutral"
        description="By updating employee details, you agree that we will verify their PAN and bank account information for accuracy and compliance."
      />
    ),
  },
  preview: {
    tableTopView: () => {
      return (
        <Alert
          isDismissible={false}
          isFullWidth
          marginTop="spacing.8"
          color="information"
          description="Please note that by updating employee details, you agree that we will verify their PAN and bank account information for accuracy and compliance."
        />
      );
    },
    sheets: {
      Employees: {
        columns: data.featureFlags?.esicMultiLocation
          ? sheet0Columns.filter((column) => column.title !== 'Location')
          : sheet0Columns,
      },
      [getContractorText(data).contractorsTitleCase]: {
        columns: sheet1Columns,
      },
    },
    permissions: [permissions.EMPLOYEE_ADD],
    feature: 'bulkOnboardingFlag',
    renderConfirmationText: (data) =>
      `Almost done! Please confirm to proceed with bulk updating employee details.`,
    successModalCtaText: 'Go to People',
    successModalMainText: "Your employee's details have been successfully updated!",
    successModalSubText: 'Need to make changes? Edit employee info anytime from the "People" page.',
    onSuccessModalCtaClick() {
      const peoplePageUrl = getPeoplePage(data.featureFlags?.employeeOnboarding);
      data.history.push(peoplePageUrl);
    },
    onDownloadErrorReport(history) {
      history?.push({
        pathname: routePaths.bulkUploads.initiate,
        search: `type=${BulkUploadTypes.TYPE_BULK_EMPLOYEE_UPDATE}`,
      });
    },
  },
});

export default config;
